import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { FiCopy, FiCheck } from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  RetriveWalletReportService,
  getfundRequestStatusService,
} from "../../../services/walletservice";
import Loader from "../../../components/Loader/Loader";
import PaginationData from "../../../components/PaginationData";
import { FaEye } from "react-icons/fa6";
import NetworkBackground from "../../LoginPage/NetworkBackground.jsx";
import { Copy } from "lucide-react";
import { showSuccessToast } from "../../../utils/toastUtils.js";

const FundManagementReport = () => {
  const [walletReportData, setWalletReportData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [orderId, setOrderId] = useState([]);
  const [paginationData, setPaginationData] = useState();
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [jsonModelReqOpen, setJsonModelReqOpen] = useState(false);
  const [jsonResponseData, setJsonResponseData] = useState(null);
  const [jsonRequestData, setJsonRequestData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [summary, setSummary] = useState(null);

  const [filterData, setFilterData] = useState({
    from_date: new Date().toISOString().split("T")[0],
    to_date: "",
    status: "",
    user_id: "",
    page: "",
    total_data: "",
    service_name: "",
    per_page: 40,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilterData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const fetChWalletData = async () => {

  setIsLoading(true);
  setLoading(true)

    try {
      const response = await RetriveWalletReportService(
        filterData,
        currentPage,
      );

      if (response.status.code === 200) {
        const reports = response?.data?.data;
        setWalletReportData(reports);
        setSummary(response?.data?.summary || null);

        // Optionally get the client_txn_id from the first QR record
        const unsettledQr = reports.find(
          (item) =>
            item.status?.value?.toLowerCase() === "unsettled" &&
            item.mode?.toLowerCase() === "qr" &&
            item.qr_payment?.client_txn_id,
        );

        if (unsettledQr) {
          setOrderId(unsettledQr.qr_payment.client_txn_id);
        } else {
          setOrderId(null); // or ""
        }

        setPaginationData(response.data.pagination);
      } else {
        setWalletReportData([]);
      }
    } catch (error) {
      console.error("Error fetching customer data:", error);
      setWalletReportData([]);
    } finally {
      setIsLoading(false);
      setLoading(false)
    }
  };

  const handlerFilterSearch = async () => {
    setIsLoading(true);
    if (searchText === " ") {
      fetChWalletData();
      setIsLoading(false);
      return;
    }

    const filterData = walletReportData.filter((curr) =>
      curr.user.user_id.includes(searchText),
    );
    setWalletReportData(filterData);
    setIsLoading(false);
    return;
  };
  const handleSetPage = (page) => {
    setCurrentPage(page);
  };
  useEffect(() => {
    fetChWalletData();
  }, [currentPage]);

  useEffect(() => {
    handlerFilterSearch();
  }, [searchText]);

  const handleGetFundStatus = async (orderId) => {
    if (!orderId) return;

    try {
      const response = await getfundRequestStatusService(orderId);
      const rawStatus =
        response?.data?.[0]?.status?.value ||
        response?.status?.message?.toLowerCase() ||
        null;

      const newStatusValue = rawStatus?.includes("failed")
        ? "failed"
        : rawStatus?.includes("pending")
          ? "pending"
          : rawStatus?.includes("unsettled")
            ? "unsettled"
            : rawStatus;

      if (newStatusValue) {
        setWalletReportData((prev) =>
          prev.map((item) =>
            item?.qr_payment?.client_txn_id === orderId
              ? {
                  ...item,
                  qr_payment: {
                    ...item.qr_payment,
                    status: {
                      ...item.qr_payment.status,
                      value: newStatusValue,
                    },
                  },
                }
              : item,
          ),
        );
      } else {
        console.warn("Status not updated – no valid value received");
      }
    } catch (error) {
      console.error("Error checking fund status:", error);
    }
  };

  const waitForNextPaint = () =>
    new Promise((resolve) => {
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => resolve());
      } else {
        setTimeout(resolve, 0);
      }
    });

  const parseJsonPayload = (jsonString, type) => {
    try {
      return jsonString ? JSON.parse(jsonString) : [];
    } catch (err) {
      console.error(`JSON parse error for ${type}:`, err);
      return jsonString || [];
    }
  };

  const handleOpenResponseModal = async (report) => {
    setIsLoading(true);
    try {
      await waitForNextPaint();
      const responseString = report?.api_response?.[0]?.response_data;
      const parsedResponseData = parseJsonPayload(responseString, "response");
      setJsonResponseData(parsedResponseData);
      setJsonModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenRequestModal = async (report) => {
    setIsLoading(true);
    try {
      await waitForNextPaint();
      const requestString = report?.api_response?.[0]?.request_data;
      const parsedRequestData = parseJsonPayload(requestString, "request");
      setJsonRequestData(parsedRequestData);
      setJsonModelReqOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("copied"));
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2 }}
      >
        {(isLoading || loading) && <Loader />}
        <div className="bg-white shadow-md rounded-lg mt-8 p-0.5">
          <h3 className="text-2xl px-2 font-bold text-blue-950 rounded-md text-center">
            Wallet Txn Report
          </h3>
          <ToastContainer position="top-right" />
          <div>
            <div className="w-full px-2 sm:px-4">
              <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mt-4 items-end">
                <div className="flex flex-col w-full">
                  <label
                    htmlFor="from_date"
                    className="text-sm font-medium text-gray-700"
                  >
                    From Date
                  </label>
                  <input
                    type="date"
                    name="from_date"
                    id="from_date"
                    className="w-full py-2 px-2 rounded border border-blue-800 outline-none shadow-sm focus:ring-2 focus:ring-blue-300"
                    value={filterData.from_date}
                    onChange={handleChange}
                  />
                </div>

                <div className="flex flex-col w-full">
                  <label
                    htmlFor="to_date"
                    className="text-sm font-medium text-gray-700"
                  >
                    To Date
                  </label>
                  <input
                    type="date"
                    name="to_date"
                    id="to_date"
                    className="w-full py-2 px-2 rounded border border-blue-800 outline-none shadow-sm focus:ring-2 focus:ring-blue-300"
                    value={filterData.to_date}
                    onChange={handleChange}
                  />
                </div>

                <div className="flex flex-col w-full">
                  <label
                    htmlFor="user_id"
                    className="text-sm font-medium text-gray-700"
                  >
                    User id
                  </label>
                  <input
                    type="text"
                    name="user_id"
                    id="user_id"
                    className="w-full py-2 px-2 rounded border border-blue-800 outline-none shadow-sm focus:ring-2 focus:ring-blue-300"
                    placeholder="AP10023"
                    value={filterData.user_id.toUpperCase()}
                    onChange={handleChange}
                  />
                </div>

                <div className="flex flex-col w-full">
                  <label
                    htmlFor="service_name"
                    className="text-sm font-medium text-gray-700"
                  >
                    Service Name
                  </label>
                  <input
                    type="text"
                    name="service_name"
                    id="service_name"
                    className="w-full py-2 px-2 rounded border border-blue-800 outline-none shadow-sm focus:ring-2 focus:ring-blue-300"
                    placeholder="Service Name"
                    value={filterData.service_name}
                    onChange={handleChange}
                  />
                </div>

                <div className="flex flex-col w-full">
                  <label
                    htmlFor="status"
                    className="text-sm font-medium text-gray-700"
                  >
                    Select Status
                  </label>
                  <select
                    name="status"
                    id="status"
                    className="w-full py-2 px-2 rounded border border-blue-800 outline-none shadow-sm focus:ring-2 focus:ring-blue-300"
                    value={filterData.status}
                    onChange={handleChange}
                  >
                    <option value="">Select Status</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>

                <button
                  type="button"
                  onClick={fetChWalletData}
                  className="w-full h-10 rounded-md px-3 text-white text-sm font-semibold shadow-lg bg-gradient-to-r from-blue-900 via-teal-400 to-purple-900 hover:opacity-90 transition"
                >
                  Submit
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-4 my-2">
              {/* Current Month */}
              <div className="flex gap-16 justify-center items-center">
                <div className="text-green-500 font-bold text-xl">
                  Credit (This Month): ₹{" "}
                  {summary?.current_month?.credit ?? "0.00"}
                </div>

                <div className="text-red-500 font-bold text-xl">
                  Debit (This Month): ₹{" "}
                  {summary?.current_month?.debit ?? "0.00"}
                </div>
              </div>

              {/* Last Month */}
              <div className="flex gap-16 justify-center items-center">
                <div className="text-green-500 font-bold text-xl">
                  Credit (Last Month): ₹ {summary?.last_month?.credit ?? "0.00"}
                </div>

                <div className="text-red-500 font-bold text-xl">
                  Debit (Last Month): ₹ {summary?.last_month?.debit ?? "0.00"}
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <div className="w-1/3">
                {/* <label htmlFor="total_data">Select Total Data</label> */}
                <select
                  name="total_data"
                  id="total_data"
                  className="border-blue-800 py-1 px-1 border rounded outline-none cursor-pointer shadow-md"
                  value={filterData.total_data}
                  onChange={handleChange}
                >
                  <option value="" disabled selected>
                    Select Total Data
                  </option>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>

              {/* <div className="w-1/5">
                  <input type="text" placeholder="Service / Name" className="border px-2 py-1 rounded-md border-blue-600 w-full"
                      value={filterData.service_name}
                      onChange={(e) => setSearchText(e.target.value.toUpperCase())}
                  />
              </div> */}
            </div>

            <div className="overflow-x-auto p-0">
              <table className="min-w-full divide-y divide-gray-200 ">
                <thead className="bg-gray-50 top-0">
                  <tr className="bg-blue-700 text-center text-white">
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      User ID
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Amount
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Gst %
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Gst Value
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Mode
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Vendor Name
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Transaction Id
                    </th>
                   
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Utr
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1 font-semibold text-white tracking-wider border-r"
                    >
                      Scope
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Txn Type
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Wallet type
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Business Wallet
                    </th>

                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Service Name
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Remarks
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Date
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      Source Req/Res
                    </th>
                    <th
                      scope="col"
                      className="px-1 text-center py-1   font-semibold text-white  tracking-wider border-r"
                    >
                      IDSPay Req/Res
                    </th>
                    {/* <th
                                            scope="col"
                                            className="px-2 text-center py-3  text-xs font-medium text-white uppercase tracking-wider">Transacted By
                                        </th> */}
                  </tr>
                </thead>
                <tbody>
                  {walletReportData.length > 0 ? (
                    walletReportData.map((report, index) => (
                      <tr key={index}>
                        <td className="text-center p-1 whitespace-nowrap text-xs text-gray-500 border-b-2 border-r">
                          {report?.user?.user_id || "N/A"}
                        </td>
                        <td className="text-center p-1 whitespace-nowrap text-xs font-bold text-gray-500 border-b-2 border-r">
                          {report?.user?.name || "N/A"}
                        </td>
                        <td className="text-center p-1 whitespace-nowrap text-sm border-b-2 border-r">
                          <span
                            className={
                              report?.factor === "Dr"
                                ? "text-red-500 font-semibold"
                                : "text-green-500 font-semibold"
                            }
                          >
                            {report?.amount || "N/A"}
                          </span>
                        </td>

                        <td className="text-center border-r p-1  whitespace-nowrap text-sm text-green-500 font-bold border-b-2">
                          {report?.gst_percentage || "N/A"}
                        </td>
                        <td className="text-center border-r p-1  whitespace-nowrap font-sm text-green-500 font-bold border-b-2">
                          {report?.gst_value || "N/A"}
                        </td>
                        <td className="text-center border-r p-1 whitespace-nowrap font-sm text-gray-500 border-b-2">
                          {report?.mode || "Manual Deposit"}
                        </td>

                         <td className="text-center border-r p-1 whitespace-nowrap text-sm text-gray-500 border-b-2 text-xs">
                          {report?.source?.vendor?.name || "N/A"}
                        </td>
                        <td className="text-center border-r p-1 whitespace-nowrap font-sm text-gray-500 border-b-2">
                          {report?.transaction_id || "Manual Deposit"}
                        </td>
                      

                        <td className="text-center border-r p-1 whitespace-nowrap text-sm text-green-500 font-bold border-b-2">
                          {" "}
                          {report?.transaction_ids[0]?.utr || "N/A"}{" "}
                        </td>

                        <td className="text-center border-r p-1 whitespace-nowrap text-sm text-gray-500 border-b-2 text-xs">
                          {report?.source?.assignment?.scope || "N/A"}
                        </td>

                        <td className="text-center border-r p-1 whitespace-nowrap text-sm border-b-2">
                          {report?.factor === "Dr" ? (
                            <span className="text-red-400 font-bold">
                              Debit
                            </span>
                          ) : (
                            <span className="text-green-500 font-bold">
                              {report?.factor === "Cr"
                                ? "Credit"
                                : report?.factor || "N/A"}
                            </span>
                          )}
                        </td>

                        {/* <td className="text-center border-r p-1  whitespace-nowrap text-sm text-green-500 font-bold border-b-2">{report?.user.balance || "N/A"}</td> */}
                        <td className="text-center border-r p-1  whitespace-nowrap text-xs text-green-500 font-bold border-b-2">
                          {report?.transaction_type || "N/A"}
                        </td>
                        <td className="text-center border-r p-1  whitespace-nowrap text-sm text-green-500 font-bold border-b-2">
                          {report?.balance_after || "N/A"}
                        </td>

                        <td className="text-center border-r p-1  whitespace-nowrap text-sm text-gray-500 border-b-2">
                          {report?.source?.service_name || "N/A"}
                        </td>
                        <td className="text-center border-r p-1  whitespace-nowrap text-xs text-blue-500 border-b-2 font-semibold">
                          {report?.narration &&
                          report?.narration !== "No narration provided." &&
                          report?.narration !== "No narration provided"
                            ? report.narration
                            : report?.remarks &&
                                report?.remarks !== "No narration provided." &&
                                report?.remarks !== "No narration provided"
                              ? report.remarks
                              : "N/A"}
                        </td>

                        <td className="text-center border-r p-1 whitespace-nowrap text-xs text-gray-500 border-b-2">
                          {report?.transaction_date || report?.created_at
                            ? new Date(
                                report?.transaction_date || report?.created_at,
                              ).toLocaleString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })
                            : "N/A"}
                        </td>

                        <td className="text-center border-r p-1 whitespace-nowrap text-sm text-gray-500 border-b-2">
                          <button
                            className={`rounded-md text-white  px-2 flex items-center justify-center gap-1 ${
                              (report?.mode?.toLowerCase() === "qr"
                                ? report?.qr_payment?.status?.value?.toLowerCase()
                                : report?.status?.value?.toLowerCase()) ===
                              "failed"
                                ? "bg-red-500"
                                : (report?.mode?.toLowerCase() === "qr"
                                      ? report?.qr_payment?.status?.value?.toLowerCase()
                                      : report?.status?.value?.toLowerCase()) ===
                                    "pending"
                                  ? "bg-yellow-500"
                                  : (report?.mode?.toLowerCase() === "qr"
                                        ? report?.qr_payment?.status?.value?.toLowerCase()
                                        : report?.status?.value?.toLowerCase()) ===
                                      "unsettled"
                                    ? "bg-blue-500"
                                    : "bg-green-500"
                            }`}
                          >
                            {report?.mode?.toLowerCase() === "qr"
                              ? report?.qr_payment?.status?.value || "N/A"
                              : report?.status?.value || "N/A"}

                            {report?.mode?.toLowerCase() === "qr" &&
                              report?.qr_payment?.status?.value?.toLowerCase() ===
                                "unsettled" &&
                              report?.qr_payment?.client_txn_id && (
                                <span
                                  onClick={() =>
                                    handleGetFundStatus(
                                      report.qr_payment.client_txn_id,
                                    )
                                  }
                                  className="ml-1 cursor-pointer text-xl"
                                  title="Check QR Status"
                                >
                                  🔄
                                </span>
                              )}
                          </button>
                        </td>

                        <td className="p-1 border hidden lg:table-cell text-center">
                          <FaEye
                            className="text-blue-600 hover:text-blue-800 cursor-pointer ml-2"
                            size={28}
                            onClick={() => handleOpenResponseModal(report)}
                          />
                        </td>

                        <td className="p-1 border hidden lg:table-cell text-center">
                          <FaEye
                            className="text-blue-600 hover:text-blue-800 cursor-pointer ml-2"
                            size={28}
                            onClick={() => handleOpenRequestModal(report)}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="text-center text-gray-500">
                        No Data Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-center my-4">
              <PaginationData
                currentPage={currentPage}
                lastPage={paginationData?.last_page ?? 1}
                handlePage={handleSetPage}
              />
            </div>
          </div>
        </div>

        <div className="p-2">
          {/* Modal */}
          {jsonModalOpen && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
              <NetworkBackground />
              <div className="bg-white rounded-2xl shadow-2xl w-[92%] max-w-4xl max-h-[95vh] overflow-y-auto border border-gray-200">
                {/* Header */}
                <div className="flex justify-between items-center bg-blue-600 text-white px-2 py-2 rounded-t-2xl">
                  <h2 className="text-lg font-semibold">📄 API Details</h2>
                  <button
                    onClick={() => setJsonModalOpen(false)}
                    className="text-white hover:text-gray-200 transition text-xl font-bold"
                  >
                    ✕
                  </button>
                </div>

                {/* Response Data Section */}
                {jsonResponseData && (
                  <div className="p-4 relative">
                    <h3 className="text-md flex justify-between items-center font-semibold text-green-600 mb-2">
                      📥 Response Data
                      <button
                        className="bg-green-100 text-green-800 p-2 border border-green-500 rounded-lg hover:border-blue-500  hover:bg-blue-600 hover:text-white"
                        onClick={() => handleCopy(jsonResponseData)}
                      >
                        <Copy size={20} />
                      </button>
                    </h3>
                    <pre className="bg-gray-900 text-green-400 text-sm rounded-lg p-2 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed shadow-inner">
                      {JSON.stringify(jsonResponseData, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Footer */}
                <div className="flex justify-end gap-2 px-2 py-1 border-t bg-gray-50 rounded-b-2xl">
                  <button
                    onClick={() => setJsonModalOpen(false)}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {jsonModelReqOpen && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
              <NetworkBackground />
              <div className="bg-white rounded-2xl shadow-2xl w-[92%] max-w-4xl max-h-[95vh] overflow-y-auto border border-gray-200">
                {/* Header */}
                <div className="flex justify-between items-center bg-blue-600 text-white px-2 py-2 rounded-t-2xl">
                  <h2 className="text-lg font-semibold">📄 API Details</h2>
                  <button
                    onClick={() => setJsonModelReqOpen(false)}
                    className="text-white hover:text-gray-200 transition text-xl font-bold"
                  >
                    ✕
                  </button>
                </div>

                {/* Request Data Section */}
                {jsonRequestData && (
                  <div className="p-4 relative border-b border-gray-300">
                    <h3 className="text-md flex justify-between items-center font-semibold text-blue-600 mb-2">
                      📤 Request Data
                      <button
                        className="bg-green-100 text-green-800 p-2 border border-green-500 rounded-lg hover:border-blue-500  hover:bg-blue-600 hover:text-white"
                        onClick={() => handleCopy(jsonRequestData)}
                      >
                        <Copy size={20} />
                      </button>
                    </h3>
                    <pre className="bg-gray-800 text-yellow-400 text-sm rounded-lg p-2 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed shadow-inner">
                      {JSON.stringify(jsonRequestData, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Footer */}
                <div className="flex justify-end gap-2 px-2 py-1 border-t bg-gray-50 rounded-b-2xl">
                  <button
                    onClick={() => setJsonModelReqOpen(false)}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
};
export default FundManagementReport;
